import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import s from './index.less';
import { Tooltip } from 'antd';
import { Icon } from '@/components';
import classNames from 'classnames';
import { ListRange, Virtuoso } from 'react-virtuoso';
import {
  useDebounceFn,
  useHover,
  useMemoizedFn,
  usePrevious,
  useThrottleEffect,
  useThrottleFn,
} from 'ahooks';
import { useIntl } from 'umi';
import classnames from 'classnames';
import BarrageItem from '../BarrageItem';

interface BarrageListProps {
  list: any[];
  onScrollToBottom?: (isBootom: boolean) => any; // 是否滑到了底部
}

const BarrageList: React.FC<BarrageListProps> = inject()(
  observer((props: BarrageListProps) => {
    const { list, onScrollToBottom } = props;
    const [isScrolling, setScrolling] = useState<boolean>(false);
    const [hasNewBarrage, setHasNewBarrage] = useState<boolean>(false);
    const [isToBottom, setIsToBottom] = useState<boolean>(false);
    const [listRange, setListRange] = useState<ListRange>({ startIndex: 10, endIndex: 10 });
    const [tempList, setTempList] = useState<any[]>([]);
    const previousList = usePrevious(list) || [];
    const oldListLength = useRef(0);
    const { formatMessage } = useIntl();
    const scrollDom = React.useRef<Virtuoso>(null);

    const $i = (mid: string, data: Record<string, string>) => {
      return formatMessage({ id: mid }, data || {});
    };

    useThrottleEffect(
      () => {
        oldListLength.current = list.length;
      },
      [list.length],
      { wait: 20, leading: false }
    );

    const loadingRef = useRef(null);
    const listRef = useRef(null);
    // 保持滚到顶部加载位置
    const holdScrollTop = useMemoizedFn(() => {
      if (isScrolling) {
        scrollDom.current?.scrollToIndex({
          index: list.length - oldListLength.current,
          behavior: 'auto',
          align: 'start',
        });
      }
    });

    // 第一条消息变化时
    useEffect(() => {
      holdScrollTop();
    }, [list?.[0]]);

    useEffect(() => {
      if (!hasNewBarrage) {
        if (
          isScrolling &&
          list.length > previousList.length &&
          !isToBottom &&
          list.length > tempList?.length
        ) {
          setHasNewBarrage(true);
        } else {
          setHasNewBarrage(false);
        }
      }
    }, [hasNewBarrage, isToBottom, list, previousList, isScrolling]);

    const { run: callBackOnScrollToBottom } = useThrottleFn(
      () => {
        onScrollToBottom?.(!isScrolling);
      },
      { wait: 1000 }
    );

    const scrollPopoToBottom = useMemoizedFn((behavior: 'smooth' | 'auto' = 'auto') => {
      scrollDom.current?.scrollToIndex({ index: list.length - 1, behavior });
      callBackOnScrollToBottom();
      setTempList(list);
    });

    const handleNewBarrageClick = () => {
      setHasNewBarrage(false);
      setIsToBottom(true);
      scrollPopoToBottom('auto');
      setTempList(list);
    };

    useThrottleEffect(
      () => {
        if (!isScrolling) {
          scrollPopoToBottom();
        }
      },
      [isScrolling, list],
      { wait: 10 }
    );

    const { run: updateIsScrolling } = useDebounceFn(
      (isScolling) => {
        setScrolling(isScolling);
      },
      { wait: 100 }
    );

    React.useEffect(() => {
      // 如果列表重置则滚到底
      if (!list.length) {
        scrollPopoToBottom();
      }
    }, [list]);

    const itemFn = useMemoizedFn((index, msg) => {
      return <BarrageItem data={msg} key={index}></BarrageItem>;
    });
    return (
      <div className={s.container}>
        <Virtuoso
          ref={scrollDom}
          data={list}
          itemContent={itemFn}
          style={{ height: '100%' }}
          className={s.scrollWrapper}
          rangeChanged={setListRange}
          atBottomStateChange={(bottom) => {
            updateIsScrolling(!bottom);
            setIsToBottom(bottom);
            if (bottom) {
              hasNewBarrage && setHasNewBarrage(false);
              setTempList(list);
            }
          }}
        />
        {hasNewBarrage && isScrolling && (
          <div
            className={classnames(s.bottomBtn, !isScrolling ? '' : '')}
            onClick={handleNewBarrageClick}
          >
            <Icon name="icon-arrow_striaght_up_1" className={s.icon} size={16} />
            <span className={s.new}>{formatMessage({ id: 'newSubtitle' })}</span>
          </div>
        )}
      </div>
    );
  })
);

export default BarrageList;
